import React from 'react';
import { Button, Drawer, MultiSelectDropdown, useToasts } from 'freemium-ui';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { CONTACT_LISTS_LIMIT } from 'routes/constants';
import { getAccountTimeFormat } from 'shared/utils';
import {
  AccountInfoType,
  ConfirmationType,
  ContactListType,
  McrContactType,
  UserType,
} from '../../../shared/types';
import { BulkUpdateType } from '../HeaderComponent/types';
import Icon from '../../../shared/components/Icon';
import {
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../shared/styles';
import {
  useGetContactListsCount,
  useSaveContactList,
} from '../../screens/lists/hooks';
import { DisplayBox } from '../../styles';

type CreateNewListButtonProps = {
  selectedRows?: McrContactType[];
  isAllSelected?: boolean;
  isLoading?: boolean;
  hideLists?: string[];
  contactLists?: ContactListType[];
  bulkUpdateSelectedContacts: (data: BulkUpdateType) => void;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
};

const CreateNewListButton = (props: CreateNewListButtonProps) => {
  const { t } = useTranslation();
  const {
    selectedRows = [],
    contactLists = [],
    isLoading,
    isAllSelected,
    bulkUpdateSelectedContacts,
    setConfirmationData,
    hideLists,
    accountInfo,
  } = props;
  const [, setToast] = useToasts();
  const [selectedLists, setSelectedLists] = React.useState<ContactListType[]>(
    [],
  );
  const [localLists, setLocalLists] = React.useState<ContactListType[]>([]);
  const [isNewListDrawerOpen, toggleNewListDrawer] =
    React.useState<boolean>(false);
  const takenNames = React.useMemo(
    () => contactLists.map((cL) => cL.label),
    [contactLists],
  );
  const { data: contactListsCountData } = useGetContactListsCount();
  const { mutate: saveContactList, isLoading: isSavingContactList } =
    useSaveContactList({
      setToast,
      callback: (isSuccess: boolean, response: ContactListType) => {
        if (isSuccess && response?.display_id)
          setSelectedLists((old) => [...(old || []), response]);
      },
    });

  const canUserAddContactLists = React.useMemo<boolean>(
    () =>
      !isNil(contactListsCountData) &&
      typeof contactListsCountData === 'number' &&
      contactListsCountData <
        (Number(accountInfo?.resource_limit_configuration?.contact_lists) ||
          CONTACT_LISTS_LIMIT),
    [contactListsCountData],
  );

  React.useEffect(() => {
    setLocalLists(
      contactLists.filter(
        (contactList) => !hideLists?.includes(contactList.display_id || ''),
      ),
    );
  }, [hideLists, contactLists]);

  const onSubmit = async () => {
    const selectedListIds = selectedLists.map((list) => list.display_id);
    bulkUpdateSelectedContacts({
      fields: {
        list_ids: selectedListIds,
      },
      callback: (isSuccess: boolean, response: boolean) => {
        if (isSuccess && response) {
          toggleNewListDrawer(false);
          setSelectedLists([]);
        }
      },
    });
  };

  const dismissDrawer = () => {
    if (selectedLists.length > 0) {
      setConfirmationData({
        id: new Date().toISOString(),
        title: `${t('forms.drawer.contact_title')}`,
        onConfirm: () => {
          toggleNewListDrawer(false);
          setSelectedLists([]);
          setConfirmationData({});
        },
        description: `${t('forms.drawer.contact_desc')}`,
      });
    } else {
      toggleNewListDrawer(false);
      setSelectedLists([]);
      setConfirmationData({});
    }
  };

  return (
    <>
      <Button
        type="secondary"
        inline
        aria-describedby="contact-lists-dropdown"
        onClick={() => toggleNewListDrawer(true)}
      >
        {t('contacts.add_to_list.title')}
      </Button>
      <Drawer
        isOpen={isNewListDrawerOpen}
        isFullHeight
        clickOutToClose={false}
        placement="right"
        onDismiss={dismissDrawer}
      >
        <FormContainer
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
          }}
        >
          <FormHeader hideBorder>
            <div className="heading">{t('contacts.add_to_list.title')}</div>
          </FormHeader>
          <FormContent>
            <FormField>
              <DisplayBox className="info">
                <div>
                  <Icon fill="#264966" width="16" height="16" glyph="info" />
                </div>
                <div className="divider" />
                <div className="text-content">
                  {isAllSelected
                    ? `${t('contacts.add_to_list.selected_contact.all')}`
                    : selectedRows.length > 1
                    ? `${t(
                        'contacts.add_to_list.selected_contact.contacts_selected',
                        {
                          count: selectedRows.length,
                        },
                      )}`
                    : `${t(
                        'contacts.add_to_list.selected_contact.contact_selected',
                      )}`}
                </div>
              </DisplayBox>
            </FormField>
            <FormField>
              <MultiSelectDropdown
                items={localLists}
                placeholder={
                  isSavingContactList
                    ? `${t('forms.placeholders.adding_contact')}`
                    : canUserAddContactLists
                    ? `${t('forms.placeholders.search_and_create_list')}`
                    : `${t('forms.placeholders.search_for_list')}`
                }
                id="modal-dd-addToLists"
                isMenuLoading={isSavingContactList}
                customValues={canUserAddContactLists}
                name="addToLists"
                label={`${t('forms.labels.contact_list')}`}
                selectedItem={selectedLists}
                maxAllowedLength={128}
                disabled={isSavingContactList}
                onCustomValueAdd={(val: ContactListType) => {
                  if (
                    takenNames.includes(val.label) ||
                    !canUserAddContactLists ||
                    val?.label?.trim() === ''
                  )
                    return;
                  saveContactList({
                    payload: {
                      description: `Created while adding to list on ${moment()
                        .tz(accountInfo?.timezone || moment.tz.guess())
                        .format(getAccountTimeFormat(accountInfo))}`,
                      label: val.label,
                    },
                  });
                }}
                onChange={(val: ContactListType[]) => {
                  setSelectedLists(val);
                }}
                itemLabelIdentifier="label"
                itemValueIdentifier="display_id"
              />
            </FormField>
          </FormContent>
          <FormActions>
            <div />
            <div>
              <Button
                inline
                htmlType="button"
                type="secondary"
                onClick={() => toggleNewListDrawer(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                inline
                htmlType="button"
                disabled={selectedLists.length === 0 || isLoading}
                onClick={() => {
                  onSubmit();
                }}
              >
                {t('add')}
              </Button>
            </div>
          </FormActions>
        </FormContainer>
      </Drawer>
    </>
  );
};

export default CreateNewListButton;
